import { useStorage } from "@vueuse/core";
import { computed } from "vue";
import {
  cloneDefaultMidiDrumMapping,
  normalizeMidiDrumMapping,
  type MidiDrumMapping,
} from "@/config/drumMapping";

export type Theme = 'light' | 'dark' | 'system';
export type SheetMusicRenderMode = 'single' | 'multi';

// State
const theme = useStorage<Theme>('theme', 'system');
const zoom = useStorage('osmd-zoom', 1.0);
const sheetMusicRenderMode = useStorage<SheetMusicRenderMode>('sheet-music-render-mode', 'single');
const sectionsEditorZoom = useStorage('sections-editor-zoom', 0.75);
const metronomeVolume = useStorage('metronome-volume', 1.0);
const audioVolume = useStorage('audio-volume', 1.0);
// Subtracted from raw MIDI event time before hit detection. Positive = user
// tends to hit late, so subtracting moves the hit earlier on the timeline and
// compensates for input/audio latency.
const midiCalibrationOffsetMs = useStorage('midi-calibration-offset-ms', 0);
// Normalizing on read migrates mappings persisted before a midi note could
// trigger two drums, when the stored shape was Record<number, DrumKey>.
const midiDrumMapping = useStorage<MidiDrumMapping>(
  'midi-drum-mapping',
  cloneDefaultMidiDrumMapping(),
  undefined,
  {
    serializer: {
      read: (raw) => normalizeMidiDrumMapping(JSON.parse(raw)),
      write: (value) => JSON.stringify(value),
    },
  }
);

// Computed
const currentTheme = computed(() => {
  if (theme.value === 'system') {
    if (typeof window === 'undefined') {
      return 'light'; // SSR fallback
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme.value;
});

export function useSettings() {
  return {
    theme,
    zoom,
    sheetMusicRenderMode,
    sectionsEditorZoom,
    metronomeVolume,
    audioVolume,
    midiCalibrationOffsetMs,
    midiDrumMapping,
    currentTheme,
  };
}